import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
	BuildRecipeParams,
	InformationRetrievalFilters,
} from "../models/InformationRetrieval";
import {
	buildRecipeFromBilletsTracked,
	getInformationRetrievalData,
	getInformationRetrievalPickers,
	getRecipesMeta,
} from "../repositories/InformationRetrievalRepository";

export const useInformationRetrievalPickers = (start: string, end: string) =>
	useQuery({
		queryKey: ["informationRetrievalPickers", start, end],
		queryFn: ({ signal }) => getInformationRetrievalPickers(start, end, signal),
		enabled: !!start && !!end,
	});

/**
 * The legacy screen only queries once a Product is chosen — its Refresh button
 * is disabled until then — so the grid stays empty rather than pulling the
 * whole mill for a date range. `selectionReady` holds it back while the
 * filters still carry a selection the new dates or shift no longer offer.
 */
export const useInformationRetrievalData = (
	filters: InformationRetrievalFilters,
	selectionReady: boolean,
) =>
	useQuery({
		queryKey: ["informationRetrieval", filters],
		queryFn: ({ signal }) =>
			getInformationRetrievalData({ ...filters, signal }),
		enabled:
			selectionReady &&
			!!filters.start &&
			!!filters.end &&
			filters.productSizeId != null,
	});

export const useRecipesMeta = () =>
	useQuery({
		queryKey: ["informationRetrievalRecipes"],
		queryFn: ({ signal }) => getRecipesMeta(signal),
	});

export const useBuildRecipe = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: BuildRecipeParams) =>
			buildRecipeFromBilletsTracked(params),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["informationRetrievalRecipes"],
			});
		},
	});
};
